import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../services/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { getUserData } from '../services/database';
import AppNavBar from './AppNavBar';
import { useModal } from '../contexts/ModalContext';

const CancelMembershipPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { showAlert } = useModal();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      if (firebaseUser) {
        try {
          const data = await getUserData(firebaseUser.uid);
          setUserData(data);
        } catch (error) {
          console.error('Error loading user data:', error);
        }
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const handleCancelSubscription = async () => {
    if (!user || !userData?.subscriptionId) {
      await showAlert('No active subscription found', 'Error');
      return;
    }

    setCancelling(true);

    try {
      const response = await fetch('/api/cancel-subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.uid,
          subscriptionId: userData.subscriptionId
        }),
      });

      const result = await response.json();

      if (response.ok) {
        await showAlert(`Subscription cancelled successfully. You will retain access until ${new Date(result.subscription.willCancelAt).toLocaleDateString()}.`, 'Cancelled');
        navigate('/my-info');
      } else {
        await showAlert('Error cancelling subscription: ' + result.error, 'Error');
      }
    } catch (error) {
      console.error('Error:', error);
      await showAlert('Error cancelling subscription. Please try again.', 'Error');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sand-100 to-sand-200">
        <AppNavBar />
        <div className="flex items-center justify-center py-12">
          <div className="bg-white rounded-lg border-4 border-ink-900 shadow-brutal p-12 w-full max-w-2xl mx-4 text-center">
            <h2 className="text-3xl font-bold text-ink-800">Loading...</h2>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sand-100 to-sand-200">
        <AppNavBar />
        <div className="flex items-center justify-center py-12">
          <div className="bg-white rounded-lg border-4 border-ink-900 shadow-brutal p-12 w-full max-w-2xl mx-4 text-center">
            <h2 className="text-3xl font-bold text-ink-800">Please log in</h2>
            <p className="text-ink-600 mt-3 text-lg">You need to be logged in to manage your subscription</p>
            <button
              onClick={() => navigate('/login')}
              className="mt-6 bg-coral-300 text-white py-2 px-6 rounded-lg hover:bg-coral-500 transition duration-200"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  const hasActiveSubscription = userData?.subscriptionTier && userData?.subscriptionTier !== 'free';

  return (
    <div className="min-h-screen bg-gradient-to-br from-sand-100 to-sand-200">
      <AppNavBar />
      <div className="flex items-center justify-center py-12">
        <div className="bg-white rounded-lg border-4 border-ink-900 shadow-brutal p-12 w-full max-w-2xl mx-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-ink-800">Cancel Membership</h2>
          </div>

          {!hasActiveSubscription ? (
            <div className="text-center">
              <p className="text-ink-600 text-lg mb-6">
                You don't have an active subscription to cancel.
              </p>
              <button
                onClick={() => navigate('/my-info')}
                className="bg-coral-300 text-white py-2 px-6 rounded-lg hover:bg-coral-500 transition duration-200"
              >
                Back to Account
              </button>
            </div>
          ) : (
            <div className="max-w-md mx-auto">
              {!showConfirmation ? (
                <div className="text-center">
                  <div className="mb-6 p-4 bg-sand-50 rounded-lg">
                    <h3 className="font-bold text-lg mb-2">Current Subscription</h3>
                    <p className="text-ink-600">
                      Plan: <span className="font-semibold">TeAIch Unlimited</span>
                    </p>
                    {userData.lastPaymentDate && (
                      <p className="text-ink-600 text-sm mt-1">
                        Last payment: {new Date(userData.lastPaymentDate.seconds * 1000).toLocaleDateString()}
                      </p>
                    )}
                  </div>

                  <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <h4 className="font-bold text-yellow-800 mb-2">What happens when you cancel?</h4>
                    <ul className="text-sm text-yellow-700 text-left list-disc pl-5 space-y-1">
                      <li>You will retain access until the end of your current billing period</li>
                      <li>No further payments will be taken</li>
                      <li>Your saved lessons and mark schemes remain in your account</li>
                      <li>You can resubscribe at any time</li>
                    </ul>
                  </div>

                  <div className="space-y-4">
                    <button
                      onClick={() => setShowConfirmation(true)}
                      className="w-full bg-red-500 text-white py-3 px-6 rounded-lg hover:bg-red-600 transition duration-200 text-lg font-semibold"
                    >
                      Cancel My Subscription
                    </button>
                    <button
                      onClick={() => navigate('/my-info')}
                      className="w-full bg-gray-300 text-gray-700 py-3 px-6 rounded-lg hover:bg-gray-400 transition duration-200 text-lg font-semibold"
                    >
                      Keep My Subscription
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center">
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <h3 className="font-bold text-red-800 text-lg mb-2">Final Confirmation</h3>
                    <p className="text-red-700">
                      Are you sure you want to cancel your subscription?
                    </p>
                  </div>

                  <div className="space-y-4">
                    <button
                      onClick={handleCancelSubscription}
                      disabled={cancelling}
                      className="w-full bg-red-600 text-white py-3 px-6 rounded-lg hover:bg-red-700 transition duration-200 text-lg font-semibold disabled:opacity-50"
                    >
                      {cancelling ? 'Cancelling...' : 'Yes, Cancel My Subscription'}
                    </button>
                    <button
                      onClick={() => setShowConfirmation(false)}
                      disabled={cancelling}
                      className="w-full bg-gray-300 text-gray-700 py-3 px-6 rounded-lg hover:bg-gray-400 transition duration-200 text-lg font-semibold disabled:opacity-50"
                    >
                      No, Keep My Subscription
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CancelMembershipPage;